/**
 * A ProseMirror plugin to detect when the cursor is bordering a widget
 * decoration and handle arrow navigation at that boundary
 *
 * This code can't reach into the widget state owned by other plugins, so to
 * opt-in, add the `data-relaxed-side="true"` data attribute to the widget
 *
 * Thanks to @marijnh for this one:
 * https://gist.github.com/marijnh/c29fcb0f2153acda43c1fa2006586380
 */

import { keydownHandler } from 'prosemirror-keymap'
import { Command, Plugin, PluginKey } from 'prosemirror-state'
import { EditorView } from 'prosemirror-view'

import { detectedPlatform } from '#hooks'

// The following commands move the browser's native selection object before and
// after a widget decoration, instead of going through the state's selection.
// This is because a widget normally aligns to one side and therefore only
// exists at a single document position in ProseMirror's state. With the
// browser's native selection, we can update this position (and therefore the
// cursor), but we have to make sure to tell the view not to sync the selection
// by calling preventDefault in the initiator

const moveSelectionBack = selectionCommand((selection) => {
  const { focusNode, focusOffset } = selection
  if (!focusNode) return false
  const widget = isAfterWidgetDecoration(focusNode, focusOffset)
  if (!widget) return false
  selection.collapse(widget.parentNode!, nodeIndexOf(widget))
  return true
})

const moveSelectionForward = selectionCommand((selection) => {
  const { focusNode, focusOffset } = selection
  if (!focusNode) return false
  const widget = isBeforeWidgetDecoration(focusNode, focusOffset)
  if (!widget) return false
  selection.collapse(widget.parentNode!, nodeIndexOf(widget) + 1)
  return true
})

export const relaxedSideWidgetHandler = new Plugin({
  key: new PluginKey('relaxed-side-widget-handler'),
  props: {
    handleDOMEvents: {
      keydown: extendedKeydownHandler({
        ArrowLeft: moveSelectionBack,
        ArrowRight: moveSelectionForward,
        ...(detectedPlatform === 'mac' && {
          'Ctrl-f': moveSelectionForward,
          'Ctrl-b': moveSelectionBack,
        }),
      }),
    },
  },
})

function extendedKeydownHandler(bindings: Record<string, Command>) {
  const handle = keydownHandler(bindings)

  return (view: EditorView, event: KeyboardEvent) => {
    const applied = handle(view, event)

    if (applied) {
      event.preventDefault()
      view.dispatch(view.state.tr.scrollIntoView())
    }

    return applied
  }
}

function isAfterWidgetDecoration(node: Node, offset: number) {
  while (!offset && node.parentNode) {
    offset = nodeIndexOf(node)
    node = node.parentNode
  }

  if (!node || !isElement(node) || !offset) return null
  const prev = node.childNodes[offset - 1]
  return isElement(prev) && isVisitable(prev) ? prev : null
}

function isBeforeWidgetDecoration(node: Node, offset: number) {
  while (offset === nodeOffset(node) && node.parentNode) {
    offset = nodeIndexOf(node) + 1
    node = node.parentNode
  }

  if (!node || !isElement(node) || offset === nodeOffset(node)) return null
  const next = node.childNodes[offset]
  return isElement(next) && isVisitable(next) ? next : null
}

function selectionCommand(fn: (selection: Selection) => boolean): Command {
  return (_state, _dispatch, view) => {
    if (!view) return false
    const win = view.dom.ownerDocument.defaultView || window
    const selection = win.getSelection()
    if (!selection) return false
    return fn(selection)
  }
}

function nodeIndexOf(node: Node) {
  if (!node.parentNode) return -1
  return Array.prototype.indexOf.call(node.parentNode.childNodes, node)
}

function nodeOffset(node: Node) {
  if (isElement(node)) return node.childNodes.length
  if (isText(node)) return node.nodeValue.length
  return 0
}

function isElement(node: Node): node is HTMLElement {
  return node.nodeType === Node.ELEMENT_NODE
}

function isText(node: Node): node is Text {
  return node.nodeType === Node.TEXT_NODE
}

function isVisitable(element: HTMLElement) {
  return element.dataset.relaxedSide === 'true'
}
